import { motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import useApiData from "../hooks/useApiData";

const fallbackNutritionPage = {
  hero: {
    badge: "",
    title: "",
    description: "",
  },
  plans: [],
};

export default function NutritionPlans() {
  const {
    data: pageData,
    loading,
    error,
  } = useApiData("/nutrition-plans", fallbackNutritionPage);

  const handleApply = () => {
    window.location.href = "/contact";
  };

  const plans = pageData?.plans || [];

  return (
    <div className="min-h-screen bg-[var(--dvx-bg)] pt-24 transition-colors duration-300">
      {/* HERO */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="text-[var(--dvx-primary)] font-oswald text-sm uppercase tracking-widest">
              {pageData?.hero?.badge}
            </span>

            <h1 className="mt-4 font-oswald text-4xl md:text-5xl lg:text-6xl font-bold text-[var(--dvx-text)] uppercase">
              {pageData?.hero?.title}
            </h1>

            <p className="mt-4 text-[var(--dvx-text-muted)] font-manrope text-lg max-w-2xl mx-auto">
              {pageData?.hero?.description}
            </p>
          </motion.div>
        </div>
      </section>

      {/* PLANS */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* LOADING */}
          {loading && (
            <div className="flex justify-center py-20">
              <div className="font-oswald text-xl text-[var(--dvx-text)] uppercase tracking-widest animate-pulse">
                Loading plans...
              </div>
            </div>
          )}

          {/* ERROR */}
          {!loading && error && (
            <div className="text-center py-20">
              <p className="text-red-500 font-oswald uppercase">
                Failed to load nutrition plans.
              </p>
              <p className="text-sm text-[var(--dvx-text-muted)] mt-2">
                Please try again later.
              </p>
            </div>
          )}

          {/* EMPTY */}
          {!loading && !error && plans.length === 0 && (
            <div className="text-center py-20">
              <h3 className="font-oswald text-2xl text-[var(--dvx-text)] uppercase">
                No Plans Found
              </h3>
              <p className="text-[var(--dvx-text-muted)] mt-2">
                No nutrition plans are available right now.
              </p>
            </div>
          )}

          {/* GRID */}
          {!loading && !error && plans.length > 0 && (
            <div className="grid md:grid-cols-3 gap-8">
              {plans.map((plan, index) => (
                <motion.div
                  key={plan.id || index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="h-full p-8 bg-[var(--dvx-surface)] border border-[var(--dvx-border)]">
                    {/* Title */}
                    <h3 className="font-oswald text-2xl font-bold text-[var(--dvx-text)] uppercase">
                      {plan.title}
                    </h3>

                    {/* Description */}
                    <p className="mt-2 text-[var(--dvx-text-muted)] text-sm">
                      {plan.description}
                    </p>

                    {/* Features */}
                    <ul className="mt-8 space-y-4">
                      {(plan.features || []).length > 0 ? (
                        plan.features.map((feature, i) => (
                          <li
                            key={i}
                            className="flex gap-3 text-[var(--dvx-text-muted)]"
                          >
                            <Check
                              size={18}
                              className="text-[var(--dvx-primary)] mt-1"
                            />
                            <span className="text-sm">{feature}</span>
                          </li>
                        ))
                      ) : (
                        <li className="text-sm text-[var(--dvx-text-muted)]">
                          No features listed.
                        </li>
                      )}
                    </ul>

                    {/* CTA */}
                    <Button
                      onClick={handleApply}
                      className="w-full mt-8 py-4 font-oswald uppercase bg-[var(--dvx-primary)] text-white"
                    >
                      Get Plan
                      <ArrowRight className="ml-2" size={18} />
                    </Button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
